import React, { useState, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate, useParams } from 'react-router-dom';
import { PAGE_REGISTRY } from './data/pageRegistry';
import Header from './components/Header';
import Footer from './components/Footer';
import FAQBanner from './components/FAQBanner';
import LangSync from './components/LangSync';
import CookieConsent from './components/CookieConsent';

const HomePage = lazy(() => import('./pages/HomePage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const PortfolioPage = lazy(() => import('./pages/PortfolioPage'));
const BlogPage = lazy(() => import('./pages/BlogPage'));
const BlogPostPage = lazy(() => import('./pages/BlogPostPage'));
const SoumissionPage = lazy(() => import('./pages/SoumissionPage'));
const ServicesHubPage = lazy(() => import('./pages/ServicesHubPage'));
const WebDevelopmentPage = lazy(() => import('./pages/WebDevelopmentPage'));
const ECommercePage = lazy(() => import('./pages/ECommercePage'));
const CustomSaaSPage = lazy(() => import('./pages/CustomSaaSPage'));
const MarketingCRMPage = lazy(() => import('./pages/MarketingCRMPage'));
const ITCloudPage = lazy(() => import('./pages/ITCloudPage'));
const PhotographyPage = lazy(() => import('./pages/PhotographyPage'));
const PubliciteAdsPage = lazy(() => import('./pages/PubliciteAdsPage'));
const LogoDesignPage = lazy(() => import('./pages/LogoDesignPage'));
const BrandingIdentityPage = lazy(() => import('./pages/BrandingIdentityPage'));
const CrunkyCatMediaPage = lazy(() => import('./pages/CrunkyCatMediaPage'));
const AicaHubPageFr = lazy(() => import('./pages/AicaHubPageFr'));
const AicaDemoPage = lazy(() => import('./pages/AicaDemoPage'));
const AicaBusinessPage = lazy(() => import('./pages/AicaBusinessPage'));
const GetYourBotPage = lazy(() => import('./pages/GetYourBotPage'));
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage'));
const TermsConditionsPage = lazy(() => import('./pages/TermsConditionsPage'));
const PolitiqueCookiesPage = lazy(() => import('./pages/PolitiqueCookiesPage'));
const ConditionsVentePage = lazy(() => import('./pages/ConditionsVentePage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));
const NewsletterUnsubscribePage = lazy(() => import('./pages/NewsletterUnsubscribePage'));
const NewsletterPage = lazy(() => import('./pages/NewsletterPage'));
const SitemapPage = lazy(() => import('./pages/SitemapPage'));
const EmailProfessionnelPage = lazy(() => import('./pages/EmailProfessionnelPage'));
const HebergementDomainesPage = lazy(() => import('./pages/HebergementDomainesPage'));
const CartesAffairesPage = lazy(() => import('./pages/CartesAffairesPage'));
const StockageCloudPage = lazy(() => import('./pages/StockageCloudPage'));
const ChatbotsMessagingPage = lazy(() => import('./pages/ChatbotsMessagingPage'));
const IntegrationIATravailPage = lazy(() => import('./pages/IntegrationIATravailPage'));
const SignatureElectroniquePage = lazy(() => import('./pages/SignatureElectroniquePage'));
const SauvegardeDonneesPage = lazy(() => import('./pages/SauvegardeDonneesPage'));
const ConseilsAffairesPage = lazy(() => import('./pages/ConseilsAffairesPage'));
const CreationContenuWebPage = lazy(() => import('./pages/CreationContenuWebPage'));
const PhotographieDomicilePage = lazy(() => import('./pages/PhotographieDomicilePage'));
const ShopifyPartnerPage = lazy(() => import('./pages/ShopifyPartnerPage'));
const BoutiquesEnLignePage = lazy(() => import('./pages/BoutiquesEnLignePage'));
const FAQPage = lazy(() => import('./pages/FAQPage'));
const CityLandingPage = lazy(() => import('./pages/CityLandingPage'));

const EN_PAGE_MAP = {
  home: HomePage,
  about: AboutPage,
  contact: ContactPage,
  portfolio: PortfolioPage,
  soumission: SoumissionPage,
  'services-hub': ServicesHubPage,
  faq: FAQPage,
  sitemap: SitemapPage,
  newsletter: NewsletterPage,
  'newsletter-unsubscribe': NewsletterUnsubscribePage,
  'web-development': WebDevelopmentPage,
  ecommerce: ECommercePage,
  'custom-saas': CustomSaaSPage,
  'marketing-crm': MarketingCRMPage,
  'it-cloud': ITCloudPage,
  photography: PhotographyPage,
  'publicite-ads': PubliciteAdsPage,
  'logo-design': LogoDesignPage,
  branding: BrandingIdentityPage,
  crunkycat: CrunkyCatMediaPage,
  'email-pro': EmailProfessionnelPage,
  'hosting-domains': HebergementDomainesPage,
  'business-cards': CartesAffairesPage,
  'cloud-storage': StockageCloudPage,
  chatbots: ChatbotsMessagingPage,
  'ai-integration': IntegrationIATravailPage,
  'e-signature': SignatureElectroniquePage,
  backup: SauvegardeDonneesPage,
  'business-advice': ConseilsAffairesPage,
  'web-content': CreationContenuWebPage,
  'home-photography': PhotographieDomicilePage,
  'shopify-stores': BoutiquesEnLignePage,
  'shopify-partner': ShopifyPartnerPage,
  'aica-hub': AicaHubPageFr,
  'aica-demo': AicaDemoPage,
  'aica-business': AicaBusinessPage,
  'aica-chatbot': GetYourBotPage,
  privacy: PrivacyPolicyPage,
  cookies: PolitiqueCookiesPage,
  terms: TermsConditionsPage,
  'terms-of-sale': ConditionsVentePage,
};

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function AppShell() {
  const [isDark, setIsDark] = useState(false);
  const { pathname } = useLocation();
  const showFaqBanner = !pathname.startsWith('/faq') && !pathname.startsWith('/en/faq');

  const toggleTheme = () => {
    setIsDark(!isDark);
    document.documentElement.classList.toggle('dark', !isDark);
  };

  return (
    <div className={`min-h-screen font-sans antialiased selection:bg-blue-500 selection:text-white overflow-x-hidden max-w-full ${isDark ? 'dark bg-slate-950 text-slate-100' : 'bg-slate-50 text-slate-900'}`}>
      <LangSync />
      <Header isDark={isDark} toggleTheme={toggleTheme} />
      <main className="pt-0">
        <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" /></div>}>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/a-propos-agence-web-sherbrooke" element={<AboutPage />} />
            <Route path="/contact-agence-web-sherbrooke" element={<ContactPage />} />
            <Route path="/portfolio-realisations-web" element={<PortfolioPage />} />
            <Route path="/blogue-technologie-web" element={<BlogPage />} />
            <Route path="/blogue-nouvelle" element={<BlogPage />} />
            <Route path="/blogue-nouvelle/:slug" element={<BlogPostPage />} />
            <Route path="/soumission-projet-web-gratuit" element={<SoumissionPage />} />
            <Route path="/services" element={<ServicesHubPage />} />
            <Route path="/services/conception-site-web-sherbrooke" element={<WebDevelopmentPage />} />
            <Route path="/services/boutique-en-ligne-ecommerce" element={<ECommercePage />} />
            <Route path="/services/developpement-saas-sur-mesure" element={<CustomSaaSPage />} />
            <Route path="/services/marketing-crm-automation" element={<MarketingCRMPage />} />
            <Route path="/services/support-informatique-microsoft365-google" element={<ITCloudPage />} />
            <Route path="/services/photographie-video-corporative" element={<PhotographyPage />} />
            <Route path="/services/publicite-google-ads-facebook" element={<PubliciteAdsPage />} />
            <Route path="/services/design-logo-entreprise" element={<LogoDesignPage />} />
            <Route path="/services/identite-visuelle-branding" element={<BrandingIdentityPage />} />
            <Route path="/services/crunkycat-media-visuels" element={<CrunkyCatMediaPage />} />
            <Route path="/services/courriels-professionnels" element={<EmailProfessionnelPage />} />
            <Route path="/services/hebergement-domaines" element={<HebergementDomainesPage />} />
            <Route path="/services/cartes-affaires" element={<CartesAffairesPage />} />
            <Route path="/services/stockage-cloud" element={<StockageCloudPage />} />
            <Route path="/services/chatbots-messagerie" element={<ChatbotsMessagingPage />} />
            <Route path="/services/integration-ia-travail" element={<IntegrationIATravailPage />} />
            <Route path="/services/signature-electronique" element={<SignatureElectroniquePage />} />
            <Route path="/services/sauvegarde-donnees" element={<SauvegardeDonneesPage />} />
            <Route path="/services/conseils-affaires" element={<ConseilsAffairesPage />} />
            <Route path="/services/creation-contenu-web" element={<CreationContenuWebPage />} />
            <Route path="/services/photographie-domicile" element={<PhotographieDomicilePage />} />
            <Route path="/Tiguidou-web-Partenaire-Shopify-vente-enligne" element={<ShopifyPartnerPage />} />
            <Route path="/services/boutiques-en-ligne-shopify" element={<BoutiquesEnLignePage />} />
            <Route path="/aica" element={<AicaHubPageFr />} />
            <Route path="/services/aica" element={<AicaHubPageFr />} />
            <Route path="/services/en-ca/aica" element={<AicaHubPageFr lang="en" />} />
            <Route path="/aica/demo-chat-intelligence-artificielle" element={<AicaDemoPage />} />
            <Route path="/aica/solutions-ia-entreprise" element={<AicaBusinessPage />} />
            <Route path="/aica/creation-chatbot-sur-mesure" element={<GetYourBotPage />} />
            <Route path="/politique-confidentialite" element={<PrivacyPolicyPage />} />
            <Route path="/politique-cookies" element={<PolitiqueCookiesPage />} />
            <Route path="/conditions-utilisation" element={<TermsConditionsPage />} />
            <Route path="/conditions-vente" element={<ConditionsVentePage />} />
            <Route path="/newsletter" element={<NewsletterPage />} />
            <Route path="/newsletter/unsubscribe" element={<NewsletterUnsubscribePage />} />
            <Route path="/faq" element={<FAQPage />} />
            <Route path="/plan-du-site" element={<SitemapPage />} />
            <Route path="/services/creation-web/:citySlug" element={<CityLandingPage lang="fr" />} />
            <Route path="/services/en/web-design/:citySlug" element={<CityRedirect />} />
            <Route path="/en/web-design/:citySlug" element={<CityLandingPage lang="en" />} />
            {PAGE_REGISTRY.filter((p) => p.en && EN_PAGE_MAP[p.id]).map((p) => {
              const Comp = EN_PAGE_MAP[p.id];
              return (
                <Route key={p.en} path={p.en} element={<Comp lang="en" />} />
              );
            })}
            <Route path="/carriere-partenariat-web" element={<NotFoundPage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
      </main>
      {showFaqBanner && <FAQBanner />}
      <Footer />
      <CookieConsent />
    </div>
  );
}

function CityRedirect() {
  const { citySlug } = useParams();
  return <Navigate to={`/en/web-design/${citySlug}`} replace />;
}

const routerBasename = (process.env.PUBLIC_URL || '').replace(/\/$/, '');

function App() {
  return (
    <Router basename={routerBasename}>
      <ScrollToTop />
      <AppShell />
    </Router>
  );
}

export default App;
